import { Descendant, Editor, Node, Point, Text } from 'slate';

/**
 * slate-react 0.65.x 的 <Slate> 在受控 value 引用变化时会直接执行 `editor.children = value`，
 * 不会同步修正 editor.selection。若此刻光标位置超出新内容（如外部把内容替换为更短的文本），
 * Editable 的选区同步 effect 会在 toDOMRange / toSlateRange 中抛错
 * （线上 Crash: Cannot resolve a DOM/Slate point ...）。
 *
 * 该插件拦截 editor.children 的「外部赋值」（即非 editor.apply 流程内的赋值），
 * 在新内容下校验并修正选区：path 不存在时收敛到文档末尾，offset 超长时收敛到文本末尾。
 */
export function withSelectionGuard<T extends Editor>(editor: T): T {
  let isApplying = false;
  const { apply } = editor;

  editor.apply = (op) => {
    isApplying = true;
    try {
      apply(op);
    } finally {
      isApplying = false;
    }
  };

  let children: Descendant[] = editor.children;

  Object.defineProperty(editor, 'children', {
    configurable: true,
    enumerable: true,
    get: () => children,
    set: (nextChildren: Descendant[]) => {
      children = nextChildren;
      if (!isApplying) {
        reconcileSelection(editor);
      }
    },
  });

  return editor;
}

function reconcileSelection(editor: Editor) {
  const { selection } = editor;
  if (!selection) {
    return;
  }

  const clampPoint = (point: Point): Point | null => {
    try {
      if (!Editor.hasPath(editor, point.path)) {
        return null;
      }
      const node = Node.get(editor, point.path);
      if (Text.isText(node) && point.offset > node.text.length) {
        return { path: point.path, offset: node.text.length };
      }
      return point;
    } catch {
      return null;
    }
  };

  const fallback = (() => {
    try {
      return Editor.end(editor, []);
    } catch {
      return null;
    }
  })();

  const anchor = clampPoint(selection.anchor) ?? fallback;
  const focus = clampPoint(selection.focus) ?? fallback;

  if (anchor === null || focus === null) {
    editor.selection = null;
    return;
  }

  if (anchor !== selection.anchor || focus !== selection.focus) {
    editor.selection = { anchor, focus };
  }
}

export default withSelectionGuard;
